/**
 * Clinic staff provider-approval status: single React Query source of truth.
 * Redirects unapproved clinic staff to /clinic-staff-pending-approval.
 * Consumers use isClinicStaffApprovalPending to show one shared loading state (e.g. with ProtectedRoute)
 * instead of a second full-page spinner at the layout level.
 */
import React, { createContext, useContext, useMemo } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useActiveRole } from './ActiveRoleContext';
import { UserProfileApi } from '../api/userProfile';
import { LoadingSpinner } from '../components/Loading/LoadingSpinner';
import { VERIFYING_ACCESS_MESSAGE } from '../components/FullPageLoader/FullPageLoader';

const CLINIC_STAFF_APPROVAL_STATUS_QUERY_KEY = ['clinic-staff-approval-status'] as const;

export interface ClinicStaffApprovalContextValue {
  isClinicStaffApprovalPending: boolean;
}

const ClinicStaffApprovalContext = createContext<ClinicStaffApprovalContextValue | undefined>(undefined);

export const ClinicStaffApprovalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { authState, user } = useAuth();
  const { activeRole } = useActiveRole();

  const isAuthenticated = authState === 'portal_authenticated' && !!user;
  const shouldFetch = isAuthenticated && !!activeRole && typeof activeRole === 'string';
  const isPendingApprovalPath = location.pathname === '/clinic-staff-pending-approval';

  const { data, isLoading } = useQuery({
    queryKey: [...CLINIC_STAFF_APPROVAL_STATUS_QUERY_KEY, activeRole],
    queryFn: () => UserProfileApi.getClinicStaffApprovalStatus(activeRole as string),
    enabled: shouldFetch,
    staleTime: 2 * 60 * 1000,
  });

  const isClinicStaffApprovalPending =
    isAuthenticated && !isPendingApprovalPath && shouldFetch && isLoading;

  const contextValue = useMemo(
    () => ({ isClinicStaffApprovalPending }),
    [isClinicStaffApprovalPending],
  );

  if (isAuthenticated && !isPendingApprovalPath && data?.isClinicStaff && !data?.isApprovedByProvider) {
    const approvalStatus = data.approvalStatus === 'Rejected' ? 'Rejected' : 'Pending';
    return <Navigate to="/clinic-staff-pending-approval" replace state={{ approvalStatus }} />;
  }

  return (
    <ClinicStaffApprovalContext.Provider value={contextValue}>
      {children}
    </ClinicStaffApprovalContext.Provider>
  );
};

export function useClinicStaffApproval(): ClinicStaffApprovalContextValue {
  const ctx = useContext(ClinicStaffApprovalContext);
  if (ctx === undefined) {
    return { isClinicStaffApprovalPending: false };
  }
  return ctx;
}

/**
 * Full-page loading while clinic staff approval is fetched (authenticated + activeRole).
 * Uses the same “Verifying your access” copy as portal validation (FullPageLoader).
 */
export const ClinicStaffApprovalLayoutGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isClinicStaffApprovalPending } = useClinicStaffApproval();
  if (isClinicStaffApprovalPending) {
    return (
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <LoadingSpinner message={VERIFYING_ACCESS_MESSAGE} ariaLabel={VERIFYING_ACCESS_MESSAGE} />
      </div>
    );
  }
  return <>{children}</>;
};
